import { Component, OnDestroy, OnInit, TrackByFunction } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { SkuPushPreviewResponse, Tpl3plService, Tpl3plSkuPushInventory } from '../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { PrintService } from '../../../services/print.service';
import { InventoryService } from '../../../services/inventory.service';

export type SkuPushInvFilter = '' | 'not_in_feed' | 'in_feed' | 'inv_error' | 'pending_only' | 'synced_only' | 'push_ok_no_inv';

/** One warehouse line in the push table — frozen on the row to avoid allocating arrays every change detection. */
export interface InvWarehouseLine {
	warehouse_code: string;
	quantity_available: number;
	quantity_on_hand: number;
	sync_status: string;
	/** Stable id for *ngFor trackBy (includes stockid + index). */
	lineKey: string;
}

export interface ManualSkuPushRow {
	stockid: string;
	description: string;
	status: 'pending' | 'sending' | 'success' | 'failed';
	message?: string;
	fullResult?: any;
	inventory: Tpl3plSkuPushInventory;
	invWarehouseLines: readonly InvWarehouseLine[];
	/** stockmaster.date_created from list API (ERP), if any. */
	dateCreatedErp: string | null;
	/** Precomputed JSON: browser→ERP and ERP→PGS request parts. */
	cachedRequestJson?: string;
	/** Precomputed JSON: PGS/HTTP response body (or ERP result envelope when no raw PGS body). */
	cachedResponseJson?: string;
	/** Tooltip text for description cell — built when row data changes. */
	detailTitleCache?: string;
}

@Component({
	selector: 'app-tpl3pl-manual-sku-push',
	templateUrl: './tpl3pl-manual-sku-push.component.html',
	styleUrls: ['./tpl3pl-manual-sku-push.component.scss']
})
export class Tpl3plManualSkuPushComponent implements OnInit, OnDestroy {
	allRows: ManualSkuPushRow[] = [];
	totalCount = 0;
	loading = false;
	loadingMore = false;
	batchRunning = false;
	batchProgress = 0;
	batchTotal = 0;
	/** SKUs per HTTP request when using Push selected / Push from row (default 25, max 100 server-side). */
	pushBatchSize = 25;
	startFrom = 0;
	private batchSessionId = 0;
	private batchHttpSub: Subscription | null = null;
	private activeBatchRows: ManualSkuPushRow[] = [];
	searchInput = '';
	csvFilter = '';
	csvSet: Set<string> | null = null;
	csvPanelOpen = false;
	statusFilter: '' | 'pending' | 'success' | 'failed' = '';
	/** Filter by tpl3pl_inventory rollup (from last inventory sync). */
	invFilter: SkuPushInvFilter = '';
	/** Server list order: SKU A–Z vs newest stockmaster.date_created first (surfaces new items). */
	listSort: 'stockid' | 'newest' = 'stockid';
	/** Limit list to SKUs whose ERP date_created is within this many days (0 = no limit). */
	createdWithinDays = 0;
	/** 0 = all brands; otherwise ERP manufacturer_id (product_to_manufacturer). */
	brandFilterManufacturerId = 0;
	/**
	 * When true, server list only includes SKUs with no tpl3pl_inventory rows (not in last inventory pull).
	 * Paging/total match this scope — use for “push only SKUs 3PL has not reported in inventory yet”.
	 */
	listNotInInventoryFeedOnly = false;
	brands: Array<{ manufacturer_id: number; name: string; mfg_code?: string }> = [];
	selected = new Set<string>();
	readonly savedResults = new Map<string, { status: ManualSkuPushRow['status']; message?: string; fullResult?: any }>();
	exporting = false;
	/** Building/downloading outbound PGS payload preview (JSON/CSV). */
	exportOutboundBusy = false;
	private readonly PAGE = 500;
	private search$ = new Subject<string>();
	private searchSub: Subscription | null = null;
	private configSub: Subscription | null = null;
	private readonly destroy$ = new Subject<void>();
	private listLoadGeneration = 0;
	private listLoadSub: Subscription | null = null;

	/** Filtered view — updated only when inputs or row push state change (not a getter). */
	filteredRows: ManualSkuPushRow[] = [];
	csvMatchCount = 0;

	constructor(
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private inventoryService: InventoryService
	) {}

	ngOnInit(): void {
		this.inventoryService.getBrands().subscribe((b: any) => {
			const raw = Array.isArray(b) ? b : [];
			this.brands = [...raw].sort((a, c) =>
				String(a?.name ?? '').localeCompare(String(c?.name ?? ''), undefined, { sensitivity: 'base' })
			);
		});
		this.searchSub = this.search$.pipe(debounceTime(350), distinctUntilChanged()).subscribe(() => this.reload());
		this.configSub = this.tpl3plService.configSaved$.subscribe(() => {
			this.reload();
		});
		this.reload();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.searchSub?.unsubscribe();
		this.configSub?.unsubscribe();
		this.batchHttpSub?.unsubscribe();
		this.listLoadSub?.unsubscribe();
	}

	trackBySku: TrackByFunction<ManualSkuPushRow> = (_index: number, row: ManualSkuPushRow) => row.stockid;

	trackByInvLineKey: TrackByFunction<InvWarehouseLine> = (_index: number, w: InvWarehouseLine) => w.lineKey;

	/** Coerced batch size for push_manual_batch (1–100). */
	get effectivePushBatchSize(): number {
		const n = Math.floor(Number(this.pushBatchSize));
		if (!Number.isFinite(n) || n < 1) {
			return 25;
		}
		return Math.min(100, n);
	}

	onSearchInput(v: string): void {
		this.searchInput = v ?? '';
		this.search$.next(this.searchInput.trim());
	}

	onStatusFilter(v: string): void {
		this.statusFilter = (v || '') as any;
		this.rebuildFilteredRows();
	}

	onInvFilter(v: string): void {
		this.invFilter = (v || '') as SkuPushInvFilter;
		this.rebuildFilteredRows();
	}

	onCsvFilter(v: string): void {
		this.csvFilter = v ?? '';
		this.csvSet = this.parseCsvSet(this.csvFilter);
		this.rebuildFilteredRows();
	}

	clearCsvFilter(): void {
		this.csvFilter = '';
		this.csvSet = null;
		this.rebuildFilteredRows();
	}

	onListSortChange(v: string): void {
		this.listSort = v === 'newest' ? 'newest' : 'stockid';
		this.reload();
	}

	onCreatedWithinChange(v: number): void {
		const n = Math.floor(Number(v));
		this.createdWithinDays = !Number.isFinite(n) || n < 0 ? 0 : Math.min(3650, n);
		this.reload();
	}

	onBrandFilterChange(v: number): void {
		const n = Math.floor(Number(v));
		this.brandFilterManufacturerId = !Number.isFinite(n) || n < 0 ? 0 : n;
		this.reload();
	}

	onListNotInInventoryFeedOnlyChange(checked: boolean): void {
		this.listNotInInventoryFeedOnly = !!checked;
		this.reload();
	}

	/** Options passed to push_list / push_preview (server-side list scope). */
	private skuPushListQueryOpts(): {
		sort: 'stockid' | 'newest';
		created_within_days: number;
		manufacturer_id?: number;
		not_in_inventory_feed?: boolean;
	} {
		const o: {
			sort: 'stockid' | 'newest';
			created_within_days: number;
			manufacturer_id?: number;
			not_in_inventory_feed?: boolean;
		} = {
			sort: this.listSort,
			created_within_days: this.createdWithinDays,
		};
		if (this.brandFilterManufacturerId > 0) {
			o.manufacturer_id = this.brandFilterManufacturerId;
		}
		if (this.listNotInInventoryFeedOnly) {
			o.not_in_inventory_feed = true;
		}
		return o;
	}

	selectFromCsv(): void {
		if (!this.csvSet || this.csvSet.size === 0) { return; }
		this.allRows.forEach(r => {
			if (this.csvSet!.has(r.stockid.toUpperCase())) {
				this.selected.add(r.stockid);
			}
		});
	}

	private parseCsvSet(raw: string): Set<string> | null {
		if (!raw.trim()) { return null; }
		const ids = raw
			.split(/[\s,]+/)
			.map(s => s.replace(/['"]/g, '').trim().toUpperCase())
			.filter(Boolean);
		return ids.length ? new Set(ids) : null;
	}

	reload(): void {
		this.listLoadGeneration++;
		const gen = this.listLoadGeneration;
		this.listLoadSub?.unsubscribe();
		this.listLoadSub = null;
		this.loading = true;
		this.allRows = [];
		this.selected.clear();
		this.rebuildFilteredRows();
		this.fetchChunk(0, gen);
	}

	private fetchChunk(offset: number, generation: number): void {
		if (generation !== this.listLoadGeneration) {
			return;
		}
		const search = (this.searchInput || '').trim() || undefined;
		this.listLoadSub?.unsubscribe();
		this.listLoadSub = this.tpl3plService
			.getSkuPushList(offset, this.PAGE, search, this.skuPushListQueryOpts())
			.pipe(takeUntil(this.destroy$))
			.subscribe({
			next: (res) => {
				if (generation !== this.listLoadGeneration) {
					return;
				}
				if (!res.success) {
					this.loading = false;
					this.loadingMore = false;
					this.globalSearchService.showNotification((res as any).message || 'Could not load SKU list', 'warning', 'bottom', 'right');
					this.rebuildFilteredRows();
					return;
				}
				this.totalCount = res.total ?? 0;
				const items = res.items || [];
				const ids = res.stockids || [];
				const chunk: ManualSkuPushRow[] = items.length
					? items.map((it) =>
							this.rowFromServer(it.stockid, it.description, it.inventory, it.date_created ?? null)
						)
					: ids.map((sid) => this.rowFromServer(sid, '', undefined, null));
				this.allRows = [...this.allRows, ...chunk];
				this.rebuildFilteredRows();
				const fetched = offset + chunk.length;
				if (chunk.length === this.PAGE && fetched < this.totalCount) {
					this.loadingMore = true;
					this.loading = false;
					this.fetchChunk(fetched, generation);
				} else {
					this.loading = false;
					this.loadingMore = false;
					this.listLoadSub = null;
				}
			},
			error: () => {
				if (generation !== this.listLoadGeneration) {
					return;
				}
				this.loading = false;
				this.loadingMore = false;
				this.listLoadSub = null;
				this.globalSearchService.showNotification('Could not load SKU list', 'danger', 'bottom', 'right');
				this.rebuildFilteredRows();
			}
		});
	}

	private defaultInventory(): Tpl3plSkuPushInventory {
		return {
			state: 'none',
			has_synced: false,
			has_error: false,
			has_pending: false,
			warehouse_count: 0,
			last_inventory_sync: null,
			total_qty_available: 0,
			total_qty_on_hand: 0,
			warehouses: [],
		};
	}

	private mergeInventory(inv?: Tpl3plSkuPushInventory): Tpl3plSkuPushInventory {
		const d = this.defaultInventory();
		if (!inv || typeof inv !== 'object') {
			return d;
		}
		const whs = Array.isArray(inv.warehouses)
			? inv.warehouses.map((w) => ({
				warehouse_code: String(w?.warehouse_code ?? ''),
				sync_status: String(w?.sync_status ?? ''),
				quantity_available: Number(w?.quantity_available) || 0,
				quantity_on_hand: Number(w?.quantity_on_hand) || 0,
			}))
			: d.warehouses;
		const sumOh = whs.reduce((s, w) => s + w.quantity_on_hand, 0);
		return {
			...d,
			...inv,
			warehouses: whs,
			warehouse_count: whs.length,
			total_qty_on_hand:
				inv.total_qty_on_hand != null && Number.isFinite(Number(inv.total_qty_on_hand))
					? Number(inv.total_qty_on_hand)
					: sumOh,
		};
	}

	private rowFromServer(
		stockid: string,
		description: string,
		inv?: Tpl3plSkuPushInventory,
		dateCreatedErp?: string | null
	): ManualSkuPushRow {
		const saved = this.savedResults.get(stockid);
		const inventory = this.mergeInventory(inv);
		const fullResult = saved?.fullResult;
		const row: ManualSkuPushRow = {
			stockid,
			description: description || '',
			status: saved?.status ?? 'pending',
			message: saved?.message,
			fullResult,
			inventory,
			invWarehouseLines: this.buildInvWarehouseLines(stockid, inventory),
			dateCreatedErp:
				dateCreatedErp != null && String(dateCreatedErp).trim() !== '' ? String(dateCreatedErp).trim() : null,
		};
		row.detailTitleCache = this.computeInvDetailTitle(row);
		this.refreshRowCachesAfterPush(row);
		return row;
	}

	private rebuildFilteredRows(): void {
		let rows = this.allRows;
		if (this.csvSet && this.csvSet.size > 0) {
			this.csvMatchCount = this.allRows.filter((r) => this.csvSet!.has(r.stockid.toUpperCase())).length;
			rows = rows.filter((r) => this.csvSet!.has(r.stockid.toUpperCase()));
		} else {
			this.csvMatchCount = 0;
			if (this.searchInput.trim()) {
				const q = this.searchInput.trim().toLowerCase();
				rows = rows.filter(
					(r) => r.stockid.toLowerCase().includes(q) || r.description.toLowerCase().includes(q)
				);
			}
		}
		if (this.statusFilter) {
			rows = rows.filter((r) => r.status === this.statusFilter);
		}
		if (this.invFilter) {
			rows = rows.filter((r) => this.rowMatchesInvFilter(r));
		}
		this.filteredRows = rows;
	}

	private buildInvWarehouseLines(stockid: string, inv: Tpl3plSkuPushInventory): readonly InvWarehouseLine[] {
		const wh = inv?.warehouses;
		if (wh && wh.length > 0) {
			return wh.map((w, i) => ({
				warehouse_code: (w.warehouse_code || '').trim() || '—',
				quantity_available: w.quantity_available ?? 0,
				quantity_on_hand: w.quantity_on_hand ?? 0,
				sync_status: (w.sync_status || '—').toString(),
				lineKey: `${stockid}|${i}|${(w.warehouse_code || '').trim() || '—'}`,
			}));
		}
		return Object.freeze([
			{
				warehouse_code: '—',
				quantity_available: 0,
				quantity_on_hand: 0,
				sync_status: '—',
				lineKey: `${stockid}|0|—`,
			},
		]);
	}

	private onRowPushStateChanged(): void {
		if (this.statusFilter || this.invFilter === 'push_ok_no_inv') {
			this.rebuildFilteredRows();
		}
	}

	private safeJsonStringify(v: unknown): string {
		try {
			return JSON.stringify(v, null, 2);
		} catch {
			return String(v);
		}
	}

	private refreshRowCachesAfterPush(row: ManualSkuPushRow): void {
		row.detailTitleCache = this.computeInvDetailTitle(row);
		row.cachedRequestJson = undefined;
		row.cachedResponseJson = undefined;
		const fr = row.fullResult;
		if (fr == null || typeof fr !== 'object') {
			return;
		}
		const o = fr as Record<string, unknown>;
		const reqEnvelope: Record<string, unknown> = {};
		if (o['client_erp_request'] != null) {
			reqEnvelope['browser_to_erp'] = o['client_erp_request'];
		} else {
			reqEnvelope['browser_to_erp'] = {
				method: 'POST',
				relative_api: 'tpl3pl/sku/push_one',
				body: { stockid: row.stockid },
			};
		}
		if (o['request_payload'] != null) {
			reqEnvelope['erp_to_pgs'] = o['request_payload'];
		}
		if (o['request'] != null) {
			reqEnvelope['http_error'] = o['request'];
		}
		row.cachedRequestJson = this.safeJsonStringify(reqEnvelope);

		if (Object.prototype.hasOwnProperty.call(o, 'response')) {
			row.cachedResponseJson = this.safeJsonStringify(o['response']);
		} else if (o['errorBody'] !== undefined) {
			row.cachedResponseJson = this.safeJsonStringify(o['errorBody']);
		} else {
			const omit = new Set(['client_erp_request', 'request_payload', 'request', 'response', 'errorBody']);
			const rest: Record<string, unknown> = {};
			for (const [k, v] of Object.entries(o)) {
				if (!omit.has(k)) {
					rest[k] = v;
				}
			}
			if (Object.keys(rest).length > 0) {
				row.cachedResponseJson = this.safeJsonStringify(rest);
			}
		}
	}

	private rowMatchesInvFilter(r: ManualSkuPushRow): boolean {
		const inv = r.inventory;
		switch (this.invFilter) {
			case 'not_in_feed':
				return inv.state === 'none';
			case 'in_feed':
				return inv.has_synced;
			case 'inv_error':
				return inv.has_error;
			case 'pending_only':
				return inv.state === 'pending';
			case 'synced_only':
				return inv.state === 'synced';
			case 'push_ok_no_inv':
				return r.status === 'success' && inv.state === 'none';
			default:
				return true;
		}
	}

	invBadgeClass(row: ManualSkuPushRow): string {
		const s = row.inventory.state;
		if (s === 'none') { return 'inv-none'; }
		if (s === 'synced') { return 'inv-synced'; }
		if (s === 'error') { return 'inv-error'; }
		if (s === 'mixed') { return 'inv-mixed'; }
		if (s === 'pending') { return 'inv-pending'; }
		return 'inv-unknown';
	}

	/** Short rollup label under description (PGS cache / last pull). */
	invRollupLabel(row: ManualSkuPushRow): string {
		switch (row.inventory.state) {
			case 'none':
				return 'Not in pull';
			case 'synced':
				return 'Synced';
			case 'error':
				return 'Error';
			case 'mixed':
				return 'Mixed';
			case 'pending':
				return 'Pending';
			default:
				return 'Unknown';
		}
	}

	invWhSyncClass(syncStatus: string): string {
		const x = (syncStatus || '').toLowerCase();
		if (x === 'synced') { return 'line-sync-synced'; }
		if (x === 'error') { return 'line-sync-error'; }
		if (x === 'pending') { return 'line-sync-pending'; }
		return 'line-sync-na';
	}

	private computeInvDetailTitle(row: ManualSkuPushRow): string {
		const inv = row.inventory;
		if (!inv.warehouses.length) {
			return 'No rows in tpl3pl_inventory — run inventory sync or SKU may not exist at 3PL yet.';
		}
		const lines = inv.warehouses.map((w) =>
			`${w.warehouse_code || '—'}: ${w.sync_status} · avail ${w.quantity_available} · 3PL OH ${w.quantity_on_hand}`
		);
		let t = lines.join('\n');
		if (inv.last_inventory_sync) {
			t += `\nLast inventory sync: ${inv.last_inventory_sync}`;
		}
		if (row.status === 'success' && inv.state === 'none') {
			t += '\n\nSKU push succeeded but this SKU is not in the last inventory pull — try inventory sync or confirm at 3PL.';
		}
		if (row.status === 'failed') {
			t += '\n\nSKU push failed — consider resending SKU.';
		}
		return t;
	}

	get csvParsedCount(): number {
		return this.csvSet?.size ?? 0;
	}

	// ── Selection ──────────────────────────────────────────────────────────────

	isSelected(id: string): boolean { return this.selected.has(id); }

	toggleRow(id: string): void {
		this.selected.has(id) ? this.selected.delete(id) : this.selected.add(id);
	}

	get allVisible(): ManualSkuPushRow[] { return this.filteredRows; }

	get allVisibleSelected(): boolean {
		const v = this.allVisible;
		return v.length > 0 && v.every(r => this.selected.has(r.stockid));
	}

	get someVisibleSelected(): boolean {
		const v = this.allVisible;
		const n = v.filter(r => this.selected.has(r.stockid)).length;
		return n > 0 && n < v.length;
	}

	toggleAll(): void {
		if (this.allVisibleSelected) {
			this.allVisible.forEach(r => this.selected.delete(r.stockid));
		} else {
			this.allVisible.forEach(r => this.selected.add(r.stockid));
		}
	}

	get selectedCount(): number { return this.selected.size; }

	private rowEligibleForBatchPush(r: ManualSkuPushRow): boolean {
		return r.status === 'pending' || r.status === 'failed';
	}

	// ── Push ───────────────────────────────────────────────────────────────────

	pushRow(row: ManualSkuPushRow): void {
		if (row.status === 'sending' || this.batchRunning) { return; }
		row.status = 'sending';
		row.message = undefined;
		this.tpl3plService.pushOneSku(row.stockid).pipe(takeUntil(this.destroy$)).subscribe({
			next: (r) => this.applyResult(row, r),
			error: (err) => this.applyError(row, err)
		});
	}

	pushSelected(): void {
		if (this.batchRunning) { return; }
		const queue = this.filteredRows.filter((r) => this.selected.has(r.stockid) && this.rowEligibleForBatchPush(r));
		if (!queue.length) {
			this.globalSearchService.showNotification('No pending or failed SKUs in selection', 'warning', 'bottom', 'right');
			return;
		}
		this.startBatch(queue);
	}

	pushFromHere(): void {
		if (this.batchRunning) { return; }
		const from = Math.max(0, Math.floor(Number(this.startFrom) || 0));
		const queue = this.filteredRows.filter((r) => this.rowEligibleForBatchPush(r)).slice(from);
		if (!queue.length) {
			this.globalSearchService.showNotification('No pending or failed SKUs in the filtered list from this offset', 'warning', 'bottom', 'right');
			return;
		}
		this.startBatch(queue);
	}

	stopBatchSend(): void {
		if (!this.batchRunning) {
			return;
		}
		this.batchSessionId++;
		const touched = [...this.activeBatchRows];
		this.batchHttpSub?.unsubscribe();
		this.batchHttpSub = null;
		for (const r of touched) {
			if (r.status === 'sending') {
				r.status = 'pending';
				r.message = undefined;
			}
		}
		this.activeBatchRows = [];
		this.batchRunning = false;
		this.onRowPushStateChanged();
		this.globalSearchService.showNotification('Stopped', 'info', 'bottom', 'right');
	}

	private startBatch(queue: ManualSkuPushRow[]): void {
		this.batchSessionId++;
		const sessionId = this.batchSessionId;
		this.batchProgress = 0;
		this.batchTotal = queue.length;
		this.batchRunning = true;
		this.activeBatchRows = [];
		this.runBatchChunk(queue, 0, sessionId);
	}

	private runBatchChunk(queue: ManualSkuPushRow[], startIdx: number, sessionId: number): void {
		if (sessionId !== this.batchSessionId) {
			return;
		}
		if (startIdx >= queue.length) {
			this.batchRunning = false;
			this.activeBatchRows = [];
			this.batchHttpSub = null;
			const ok = queue.filter((r) => r.status === 'success').length;
			const fail = queue.filter((r) => r.status === 'failed').length;
			this.globalSearchService.showNotification(
				`Done — ${ok} ok${fail ? ', ' + fail + ' failed' : ''}`,
				fail ? 'warning' : 'success',
				'bottom',
				'right'
			);
			return;
		}
		const size = this.effectivePushBatchSize;
		const chunk = queue.slice(startIdx, startIdx + size);
		this.activeBatchRows = chunk;
		for (const row of chunk) {
			row.status = 'sending';
			row.message = undefined;
		}
		const ids = chunk.map((r) => r.stockid);
		this.batchHttpSub = this.tpl3plService
			.pushManualSkuBatch(ids)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
			next: (res) => {
				this.batchHttpSub = null;
				if (sessionId !== this.batchSessionId) {
					return;
				}
				if (!res.success || !Array.isArray(res.results)) {
					for (const row of chunk) {
						this.applyResult(
							row,
							{ success: false, message: res.message || 'Batch request failed' },
							{ skipFilterRebuild: true, batchStockids: ids }
						);
					}
					this.onRowPushStateChanged();
					this.batchRunning = false;
					this.activeBatchRows = [];
					this.globalSearchService.showNotification(res.message || 'Batch request failed', 'danger', 'bottom', 'right');
					return;
				}
				const byId = new Map(res.results.map((x) => [String(x.stockid ?? ''), x]));
				for (const row of chunk) {
					const item = byId.get(row.stockid);
					if (item) {
						this.applyResult(row, item, { skipFilterRebuild: true, batchStockids: ids });
					} else {
						this.applyResult(
							row,
							{ success: false, message: 'No result from server', response: null },
							{ skipFilterRebuild: true, batchStockids: ids }
						);
					}
				}
				this.onRowPushStateChanged();
				this.batchProgress = Math.min(startIdx + chunk.length, queue.length);
				this.runBatchChunk(queue, startIdx + chunk.length, sessionId);
			},
			error: (err) => {
				this.batchHttpSub = null;
				if (sessionId !== this.batchSessionId) {
					return;
				}
				for (const row of chunk) {
					this.applyError(row, err, { skipFilterRebuild: true, batchStockids: ids });
				}
				this.onRowPushStateChanged();
				this.batchRunning = false;
				this.activeBatchRows = [];
				this.globalSearchService.showNotification('Batch request failed', 'danger', 'bottom', 'right');
			},
		});
	}

	private pgsMessageImpliesSkuAlreadySubmitted(msg: unknown): boolean {
		const m = String(msg ?? '').toLowerCase();
		if (!m) {
			return false;
		}
		return (
			m.includes('already exist') ||
			m.includes('duplicate') ||
			m.includes('already registered') ||
			m.includes('sku exist') ||
			m.includes('under review')
		);
	}

	private pgsResponseBodyImpliesSkuAlreadySubmitted(body: unknown): boolean {
		if (!body || typeof body !== 'object') {
			return false;
		}
		const o = body as Record<string, unknown>;
		const parts: string[] = [];
		for (const k of ['Message', 'message', 'Description', 'description']) {
			if (o[k] != null) {
				parts.push(String(o[k]).toLowerCase());
			}
		}
		const err = o['Error'];
		if (err && typeof err === 'object') {
			const e = err as Record<string, unknown>;
			for (const k of ['Message', 'MessageDetail', 'message']) {
				if (e[k] != null) {
					parts.push(String(e[k]).toLowerCase());
				}
			}
		}
		const t = parts.join(' ');
		const needles = [
			'under review',
			'already exist',
			'already exists',
			'duplicate',
			'already registered',
			'sku exist',
			'already in use',
			'already submitted',
			'being reviewed',
		];
		return needles.some((n) => t.includes(n));
	}

	private applyResult(
		row: ManualSkuPushRow,
		r: any,
		opts?: { skipFilterRebuild?: boolean; batchStockids?: string[] }
	): void {
		const serverOk = !!(r && typeof r === 'object' && r.success);
		const impliedOk =
			!serverOk &&
			(this.pgsMessageImpliesSkuAlreadySubmitted(r?.message) ||
				this.pgsResponseBodyImpliesSkuAlreadySubmitted(r?.response));
		const ok = serverOk || impliedOk;
		row.status = ok ? 'success' : 'failed';
		if (ok) {
			if (serverOk) {
				row.message = r?.message != null ? String(r.message) : 'OK';
			} else {
				const errMsg = r?.response?.Error?.Message;
				row.message =
					errMsg != null && String(errMsg).trim() !== ''
						? `Already at 3PL: ${String(errMsg)}`
						: 'SKU already submitted at 3PL (treated as success)';
			}
		} else {
			row.message = r?.message != null ? String(r.message) : 'Failed';
		}
		const base = impliedOk ? { ...r, success: true, treated_as_success: true } : r;
		const clientErp =
			opts?.batchStockids != null && opts.batchStockids.length > 0
				? { method: 'POST', relative_api: 'tpl3pl/sku/push_manual_batch', body: { stockids: opts.batchStockids } }
				: { method: 'POST', relative_api: 'tpl3pl/sku/push_one', body: { stockid: row.stockid } };
		row.fullResult = { ...base, client_erp_request: clientErp };
		this.savedResults.set(row.stockid, { status: row.status, message: row.message, fullResult: row.fullResult });
		this.refreshRowCachesAfterPush(row);
		if (!opts?.skipFilterRebuild) {
			this.onRowPushStateChanged();
		}
	}

	private applyError(row: ManualSkuPushRow, err: any, opts?: { skipFilterRebuild?: boolean; batchStockids?: string[] }): void {
		row.status = 'failed';
		const body = err?.error;
		row.message =
			(typeof body === 'object' && body != null && body.message != null)
				? String(body.message)
				: (typeof body === 'string' ? body : err?.message || 'Request failed');
		const clientErp =
			opts?.batchStockids != null && opts.batchStockids.length > 0
				? { method: 'POST', relative_api: 'tpl3pl/sku/push_manual_batch', body: { stockids: opts.batchStockids } }
				: { method: 'POST', relative_api: 'tpl3pl/sku/push_one', body: { stockid: row.stockid } };
		row.fullResult = {
			success: false,
			message: row.message,
			httpStatus: err?.status,
			errorBody: body,
			request: err?.url ? { url: err.url, method: err.method } : undefined,
			client_erp_request: clientErp,
		};
		this.savedResults.set(row.stockid, { status: 'failed', message: row.message, fullResult: row.fullResult });
		this.refreshRowCachesAfterPush(row);
		if (!opts?.skipFilterRebuild) {
			this.onRowPushStateChanged();
		}
	}

	// ── Export outbound PGS payload (preview — no call to PGS) ───────────────────

	private static readonly OUTBOUND_PREVIEW_CHUNK = 5000;

	/** Exact JSON bodies that would be POSTed to PGS /api/Sku. Uses selected SKUs if any; else all rows matching server list filters (not the table description-only filter). */
	exportOutboundJson(): void {
		if (this.exportOutboundBusy) {
			return;
		}
		const selectedIds = Array.from(this.selected);
		if (selectedIds.length > 0) {
			if (selectedIds.length > 2000) {
				this.globalSearchService.showNotification(
					'Select at most 2000 SKUs for outbound export (server limit).',
					'warning',
					'bottom',
					'right'
				);
				return;
			}
			this.runOutboundExportForStockids(selectedIds, 'json');
			return;
		}
		this.runOutboundExportPaged('json');
	}

	/** Same data as JSON export, flattened to CSV (SKU, UPC, descriptions, CompanyID, dimensions, etc.). */
	exportOutboundCsv(): void {
		if (this.exportOutboundBusy) {
			return;
		}
		const selectedIds = Array.from(this.selected);
		if (selectedIds.length > 0) {
			if (selectedIds.length > 2000) {
				this.globalSearchService.showNotification(
					'Select at most 2000 SKUs for outbound export (server limit).',
					'warning',
					'bottom',
					'right'
				);
				return;
			}
			this.runOutboundExportForStockids(selectedIds, 'csv');
			return;
		}
		this.runOutboundExportPaged('csv');
	}

	private runOutboundExportForStockids(stockids: string[], kind: 'json' | 'csv'): void {
		this.exportOutboundBusy = true;
		this.tpl3plService.getSkuPushPreview({ stockids }).pipe(takeUntil(this.destroy$)).subscribe({
			next: (res) => this.finalizeOutboundExport(res, kind, stockids.length, { mergedPages: 1 }),
			error: () => {
				this.exportOutboundBusy = false;
				this.globalSearchService.showNotification('Outbound export failed', 'danger', 'bottom', 'right');
			},
		});
	}

	private runOutboundExportPaged(kind: 'json' | 'csv'): void {
		this.exportOutboundBusy = true;
		const search = (this.searchInput || '').trim() || undefined;
		const chunk = Tpl3plManualSkuPushComponent.OUTBOUND_PREVIEW_CHUNK;
		let offset = 0;
		const allRequests: NonNullable<SkuPushPreviewResponse['requests']> = [];
		const allSkipped: NonNullable<SkuPushPreviewResponse['skipped']> = [];
		let mergedMeta: SkuPushPreviewResponse['meta'] | undefined;
		let pages = 0;

		const pull = (): void => {
			this.tpl3plService
				.getSkuPushPreview({
					offset,
					limit: chunk,
					search,
					...this.skuPushListQueryOpts(),
				})
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					next: (res) => {
						if (!res.success) {
							this.exportOutboundBusy = false;
							this.globalSearchService.showNotification(res.message || 'Preview failed', 'warning', 'bottom', 'right');
							return;
						}
						pages++;
						if (!mergedMeta) {
							mergedMeta = res.meta ? { ...res.meta } : undefined;
						}
						if (res.requests?.length) {
							allRequests.push(...res.requests);
						}
						if (res.skipped?.length) {
							allSkipped.push(...res.skipped);
						}
						if (res.meta?.has_more) {
							offset = res.meta.next_offset != null ? res.meta.next_offset : offset + (res.meta.rows_fetched ?? chunk);
							pull();
						} else {
							this.finalizeOutboundExport(
								{
									success: true,
									meta: mergedMeta
										? {
												...mergedMeta,
												returned: allRequests.length,
												skipped_count: allSkipped.length,
												has_more: false,
												next_offset: null,
												note: `Merged ${pages} preview page(s).`,
											}
										: undefined,
									requests: allRequests,
									skipped: allSkipped,
								},
								kind,
								allRequests.length + allSkipped.length,
								{ mergedPages: pages }
							);
						}
					},
					error: () => {
						this.exportOutboundBusy = false;
						this.globalSearchService.showNotification('Outbound export failed', 'danger', 'bottom', 'right');
					},
				});
		};
		pull();
	}

	private finalizeOutboundExport(
		res: SkuPushPreviewResponse,
		kind: 'json' | 'csv',
		approxTotal: number,
		extra: { mergedPages: number }
	): void {
		this.exportOutboundBusy = false;
		if (!res.success || !res.requests?.length) {
			const msg =
				res.skipped?.length && !res.requests?.length
					? 'No buildable payloads (see skipped in response).'
					: res.message || 'Nothing to export';
			this.globalSearchService.showNotification(msg, 'warning', 'bottom', 'right');
			return;
		}
		const ts = new Date().toISOString().slice(0, 19).replace(/[T:-]/g, '');
		if (kind === 'json') {
			const doc = {
				exported_at: new Date().toISOString(),
				note: 'Each request is the exact POST /api/Sku body (plus method/path) the server sends after Bearer auth.',
				meta: { ...(res.meta || {}), export_merged_pages: extra.mergedPages, row_hint: approxTotal },
				requests: res.requests,
				skipped: res.skipped && res.skipped.length ? res.skipped : undefined,
			};
			this.downloadTextFile(`tpl3pl_sku_outbound_${ts}.json`, JSON.stringify(doc, null, 2), 'application/json;charset=utf-8');
		} else {
			const csv = Tpl3plManualSkuPushComponent.buildOutboundCsv(res.requests);
			this.downloadTextFile(`tpl3pl_sku_outbound_${ts}.csv`, csv, 'text/csv;charset=utf-8');
		}
		this.globalSearchService.showNotification(
			`Exported ${res.requests.length} SKU payload(s)` +
				(res.skipped?.length ? ` (${res.skipped.length} skipped)` : ''),
			'success',
			'bottom',
			'right'
		);
	}

	private downloadTextFile(filename: string, content: string, mime: string): void {
		const blob = new Blob([content], { type: mime });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		a.click();
		URL.revokeObjectURL(url);
	}

	private static csvCell(v: unknown): string {
		const s = v === null || v === undefined ? '' : String(v);
		if (/[",\n\r]/.test(s)) {
			return '"' + s.replace(/"/g, '""') + '"';
		}
		return s;
	}

	private static buildOutboundCsv(
		requests: NonNullable<SkuPushPreviewResponse['requests']>
	): string {
		const headers = [
			'stockid_erp',
			'sku',
			'upc',
			'masterCaseUpc',
			'shortDescription',
			'longDescription',
			'CompanyID',
			'itemTypeCode',
			'lotControl',
			'isHazmat',
			'skuUom',
			'piecesPerCase',
			'casesPerPallet',
			'pieceWeight',
			'pieceWeightUOM',
			'measureUnit',
			'stockReleaseMethod',
			'masterCaseLength',
			'masterCaseWidth',
			'masterCaseHeight',
		];
		const lines = [headers.join(',')];
		for (const r of requests) {
			const b = r.body || {};
			const row = [
				r.stockid ?? '',
				b['sku'],
				b['upc'],
				b['masterCaseUpc'],
				b['shortDescription'],
				b['longDescription'],
				b['CompanyID'],
				b['itemTypeCode'],
				b['lotControl'],
				b['isHazmat'],
				b['skuUom'],
				b['piecesPerCase'],
				b['casesPerPallet'],
				b['pieceWeight'],
				b['pieceWeightUOM'],
				b['measureUnit'],
				b['stockReleaseMethod'],
				b['masterCaseLength'],
				b['masterCaseWidth'],
				b['masterCaseHeight'],
			].map((x) => Tpl3plManualSkuPushComponent.csvCell(x));
			lines.push(row.join(','));
		}
		return lines.join('\r\n');
	}

	// ── Export (PrintService xls/pdf — same pattern as user-groups / cogs-gl-postings) ──

	private exportBaseFilename(): string {
		return 'manual_sku_push_' + new Date().toISOString().slice(0, 19).replace(/[T:-]/g, '');
	}

	private buildExportRows(): Array<{
		stockid: string;
		pushStatus: string;
		message: string;
		invState: string;
		invLastSync: string;
		invQtyAvail: string;
		responseText: string;
	}> {
		const invByStock = new Map(this.allRows.map(r => [r.stockid, r.inventory]));
		const rows: Array<{
			stockid: string;
			pushStatus: string;
			message: string;
			invState: string;
			invLastSync: string;
			invQtyAvail: string;
			responseText: string;
		}> = [];
		for (const [stockid, v] of this.savedResults.entries()) {
			const inv = invByStock.get(stockid);
			let responseText = '';
			try {
				responseText = v.fullResult != null ? JSON.stringify(v.fullResult) : '';
			} catch {
				responseText = String(v.fullResult);
			}
			if (responseText.length > 2000) {
				responseText = responseText.slice(0, 2000) + '…';
			}
			rows.push({
				stockid,
				pushStatus: v.status,
				message: v.message ?? '',
				invState: inv?.state ?? 'none',
				invLastSync: inv?.last_inventory_sync != null ? String(inv.last_inventory_sync) : '',
				invQtyAvail:
					inv != null && inv.warehouse_count > 0
						? `A${inv.total_qty_available}/OH${inv.total_qty_on_hand}`
						: (inv != null ? String(inv.state) : ''),
				responseText,
			});
		}
		rows.sort((a, b) => a.stockid.localeCompare(b.stockid));
		return rows;
	}

	private static escHtml(v: unknown): string {
		return String(v ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	private generateExportHtml(): Promise<string> {
		return new Promise((resolve) => {
			const data = this.buildExportRows();
			const title = 'Manual SKU push — results';
			const subtitle = 'Exported ' + new Date().toLocaleString() + ' · ' + data.length + ' row(s)';
			const th = ['SKU', 'Push status', 'Message', 'Inventory state', 'Inv last sync', '3PL cache avail/OH', 'API response (truncated)'];
			const trs = data.map(r =>
				'<tr>' +
				`<td>${Tpl3plManualSkuPushComponent.escHtml(r.stockid)}</td>` +
				`<td>${Tpl3plManualSkuPushComponent.escHtml(r.pushStatus)}</td>` +
				`<td>${Tpl3plManualSkuPushComponent.escHtml(r.message)}</td>` +
				`<td>${Tpl3plManualSkuPushComponent.escHtml(r.invState)}</td>` +
				`<td>${Tpl3plManualSkuPushComponent.escHtml(r.invLastSync)}</td>` +
				`<td>${Tpl3plManualSkuPushComponent.escHtml(r.invQtyAvail)}</td>` +
				`<td style="white-space:pre-wrap;max-width:480px;font-size:11px;">${Tpl3plManualSkuPushComponent.escHtml(r.responseText)}</td>` +
				'</tr>'
			).join('');
			const html =
				'<div class="table">' +
				`<div style="font-size:18px;font-weight:bold;margin-bottom:4px;">${Tpl3plManualSkuPushComponent.escHtml(title)}</div>` +
				`<div style="margin-bottom:12px;">${Tpl3plManualSkuPushComponent.escHtml(subtitle)}</div>` +
				'<table class="table table-striped table-sm table-bordered" style="border-collapse:collapse;width:100%;">' +
				'<thead><tr>' + th.map(h => `<th style="text-align:left;padding:6px;border:1px solid #ccc;">${Tpl3plManualSkuPushComponent.escHtml(h)}</th>`).join('') + '</tr></thead>' +
				'<tbody>' + trs + '</tbody></table></div>';
			resolve(html);
		});
	}

	exportXls(): void {
		if (this.savedResults.size === 0) {
			this.globalSearchService.showNotification('Nothing pushed yet', 'warning', 'bottom', 'right');
			return;
		}
		this.exporting = true;
		const filename = this.exportBaseFilename();
		const today = new Date();
		this.generateExportHtml().then((html) => {
			const data = {
				content: this.globalSearchService.base64encode(html),
				filename,
				title: 'Manual SKU push — results',
				subtitle: 'Created ' + today.toLocaleString(),
			};
			this.printService
				.xls(data)
				.pipe(takeUntil(this.destroy$))
				.subscribe({
				next: (result: any) => {
					this.exporting = false;
					if (result?.content) {
						this.globalSearchService.downloadXls(result.content, filename + '.xlsx');
					} else {
						this.globalSearchService.showNotification('Excel export returned no file', 'warning', 'bottom', 'right');
					}
				},
				error: () => {
					this.exporting = false;
					this.globalSearchService.showNotification('Excel export failed', 'danger', 'bottom', 'right');
				},
			});
		}).catch(() => {
			this.exporting = false;
		});
	}

	exportPdf(): void {
		if (this.savedResults.size === 0) {
			this.globalSearchService.showNotification('Nothing pushed yet', 'warning', 'bottom', 'right');
			return;
		}
		this.exporting = true;
		const filename = this.exportBaseFilename();
		const today = new Date();
		this.generateExportHtml().then((html) => {
			const data = {
				content: this.globalSearchService.base64encode(html),
				filename,
				title: 'Manual SKU push — results',
				subtitle: 'Created ' + today.toLocaleString(),
				landscape: true,
			};
			this.printService
				.pdf(data)
				.pipe(takeUntil(this.destroy$))
				.subscribe({
				next: (result: any) => {
					this.exporting = false;
					if (result?.content) {
						this.globalSearchService.downloadPdf(result.content, filename + '.pdf');
					} else {
						this.globalSearchService.showNotification('PDF export returned no file', 'warning', 'bottom', 'right');
					}
				},
				error: () => {
					this.exporting = false;
					this.globalSearchService.showNotification('PDF export failed', 'danger', 'bottom', 'right');
				},
			});
		}).catch(() => {
			this.exporting = false;
		});
	}

	// ── Helpers ────────────────────────────────────────────────────────────────

	rowClass(row: ManualSkuPushRow): string {
		if (row.status === 'success') { return 'row-ok'; }
		if (row.status === 'failed')  { return 'row-fail'; }
		if (row.status === 'sending') { return 'row-sending'; }
		return '';
	}

	statusBadgeClass(row: ManualSkuPushRow): string {
		if (row.status === 'success') { return 'badge-ok'; }
		if (row.status === 'failed')  { return 'badge-fail'; }
		if (row.status === 'sending') { return 'badge-sending'; }
		return 'badge-pending';
	}

	get successCount(): number {
		let n = 0;
		for (const v of this.savedResults.values()) { if (v.status === 'success') { n++; } }
		return n;
	}

	get failedCount(): number {
		let n = 0;
		for (const v of this.savedResults.values()) { if (v.status === 'failed') { n++; } }
		return n;
	}

	copyRequestJson(row: ManualSkuPushRow, ev: Event): void {
		ev.stopPropagation();
		ev.preventDefault();
		const text = row.cachedRequestJson ?? '';
		if (!text) { return; }
		navigator.clipboard?.writeText(text).then(
			() => this.globalSearchService.showNotification('Request copied', 'success', 'bottom', 'right'),
			() => this.globalSearchService.showNotification('Copy failed', 'warning', 'bottom', 'right')
		);
	}

	copyResponseJson(row: ManualSkuPushRow, ev: Event): void {
		ev.stopPropagation();
		ev.preventDefault();
		const text = row.cachedResponseJson ?? '';
		if (!text) { return; }
		navigator.clipboard?.writeText(text).then(
			() => this.globalSearchService.showNotification('Response copied', 'success', 'bottom', 'right'),
			() => this.globalSearchService.showNotification('Copy failed', 'warning', 'bottom', 'right')
		);
	}

	copyRequestAndResponseJson(row: ManualSkuPushRow, ev: Event): void {
		ev.stopPropagation();
		ev.preventDefault();
		const parseCached = (s: string | undefined): unknown => {
			if (s == null || s === '') {
				return null;
			}
			try {
				return JSON.parse(s) as unknown;
			} catch {
				return s;
			}
		};
		const combined = {
			request: parseCached(row.cachedRequestJson),
			response: parseCached(row.cachedResponseJson),
		};
		const text = this.safeJsonStringify(combined);
		if (!text.trim()) {
			return;
		}
		navigator.clipboard?.writeText(text).then(
			() => this.globalSearchService.showNotification('Request + response copied', 'success', 'bottom', 'right'),
			() => this.globalSearchService.showNotification('Copy failed', 'warning', 'bottom', 'right')
		);
	}

	get loadedLabel(): string {
		if (this.loading) { return 'Loading…'; }
		if (this.loadingMore) { return `Loading… ${this.allRows.length.toLocaleString()} of ${this.totalCount.toLocaleString()}`; }
		const shown = this.filteredRows.length;
		const total = this.allRows.length;
		if (shown === total) { return `${total.toLocaleString()} SKUs`; }
		return `${shown.toLocaleString()} shown of ${total.toLocaleString()}`;
	}
}
